// components/LinkedAvatar.js — Design system fix
// - Campaign avatar bgcolor was hardcoded '#667eea' (purple) — replaced with theme.palette.primary.main
// - Everything else unchanged

import React from "react";
import { Avatar, Link } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { alpha, useTheme } from "@mui/material/styles";
import {
  getCleanDisplayName,
  isCreatorActivity,
  getCreatorIdentifier,
  getCampaignCompany,
} from "./activityHelpers";

export default function LinkedAvatar({ activity, ...avatarProps }) {
  const theme = useTheme();
  const { user_id, pfphref, is_private, activity_type } = activity;
  const displayName = getCleanDisplayName(activity);

  /* ── Campaign: show client company avatar ──────────── */
  if (activity_type === "Campaign") {
    const company = getCampaignCompany(activity);
    if (company) {
      return (
        <Avatar
          src={company.photo || ""}
          alt={company.name}
          {...avatarProps}
          sx={{
            ...avatarProps.sx,
            bgcolor: theme.palette.primary.main,   // was '#667eea' (hardcoded purple)
            fontWeight: 700,
            fontSize: "1rem",
          }}
        >
          {!company.photo && company.name?.[0]?.toUpperCase()}
        </Avatar>
      );
    }
    // No company resolved — fall through to normal avatar
    return (
      <Avatar
        src={pfphref}
        alt={displayName}
        {...avatarProps}
        sx={{ ...avatarProps.sx }}
      />
    );
  }

  /* ── Standard avatar ───────────────────────────────── */
  const avatar = (
    <Avatar
      src={pfphref}
      alt={displayName}
      {...avatarProps}
      sx={{
        ...avatarProps.sx,
        ...(is_private && {
          opacity: 0.75,
          border: `2px solid ${alpha(theme.palette.divider, 0.5)}`,
        }),
      }}
    />
  );

  if (is_private || displayName === "Anonymous User") return avatar;

  if (isCreatorActivity(activity)) {
    const creatorIdentifier = getCreatorIdentifier(activity);
    if (creatorIdentifier) {
      return (
        <Link
          component={RouterLink}
          to={`/profile/${creatorIdentifier}`}
          sx={{ textDecoration: "none" }}
        >
          {avatar}
        </Link>
      );
    }
  } else if (user_id) {
    return (
      <Link
        component={RouterLink}
        to={`/profile/${user_id}`}
        sx={{ textDecoration: "none" }}
      >
        {avatar}
      </Link>
    );
  }

  return avatar;
}